'use client'

import * as React from 'react'
import { createContext, useContext, useState, useCallback } from 'react'
import { cn } from '@/lib/utils'

interface TabsContextValue {
  activeValue: string
  onValueChange: (value: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

function useTabsContext() {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('Tabs compound components must be used within <Tabs>')
  return ctx
}

export interface TabsProps {
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
  className?: string
}

const TabsRoot: React.FC<TabsProps> = ({ defaultValue = '', value, onValueChange, children, className }) => {
  const [internalValue, setInternalValue] = useState(defaultValue)
  const activeValue = value !== undefined ? value : internalValue

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (value === undefined) setInternalValue(newValue)
      onValueChange?.(newValue)
    },
    [value, onValueChange]
  )

  return (
    <TabsContext.Provider value={{ activeValue, onValueChange: handleValueChange }}>
      <div className={cn('', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  )
}

interface TabsListProps {
  children: React.ReactNode
  className?: string
}

const TabsList: React.FC<TabsListProps> = ({ children, className }) => (
  <div
    className={cn('flex border-b border-[var(--color-border)]', className)}
    role="tablist"
  >
    {children}
  </div>
)

interface TabsTriggerProps {
  value: string
  children: React.ReactNode
  disabled?: boolean
  className?: string
}

const TabsTrigger: React.FC<TabsTriggerProps> = ({ value, children, disabled = false, className }) => {
  const { activeValue, onValueChange } = useTabsContext()
  const isActive = activeValue === value

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      disabled={disabled}
      className={cn(
        'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-[1px]',
        isActive
          ? 'border-[var(--color-lime)] text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-[var(--color-border-light)]',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={() => !disabled && onValueChange(value)}
    >
      {children}
    </button>
  )
}

interface TabsContentProps {
  value: string
  children: React.ReactNode
  className?: string
}

const TabsContent: React.FC<TabsContentProps> = ({ value, children, className }) => {
  const { activeValue } = useTabsContext()
  if (activeValue !== value) return null

  return (
    <div
      role="tabpanel"
      className={cn('pt-4', className)}
    >
      {children}
    </div>
  )
}

const Tabs = Object.assign(TabsRoot, {
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
})

export { Tabs }
